<template>
  <div class="bg-gray-50/50 min-h-screen">
    <div fluid>
      <!-- No Repository State -->
      <div v-if="!hasRepository && !loading">
        <NoData
          title="No Repository Found"
          subTitle="Create a repository to organize and manage your content"
          btnText="Create Repository"
          :btnAction="openCreateRepoDialog"
        />
      </div>

      <!-- Google Drive style Explorer UI -->
      <v-row class="fill-height" no-gutters v-else-if="!loading">
        <!-- Left Sidebar: Folder Directory -->
        <v-col cols="12" md="3" class="pr-md-4 mb-4 mb-md-0">
          <v-card
            class="rounded-md border border-gray-100 flex flex-column h-full p-4 bg-white shadow-sm"
            flat
            outlined
          >
            <div class="mb-4">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-md font-bold text-gray-800 flex items-center">
                  <v-icon left color="#21427d" class="mr-2">mdi-folder-multiple</v-icon>
                  Directories
                </h3>
              </div>
              <v-btn
                color="primary"
                class="w-full normal-case rounded-lg"
                @click="showAddCategoryDialog = true"
                block
                outlined
              >
                <v-icon left>mdi-plus</v-icon> New Folder
              </v-btn>
            </div>

            <v-list class="flex-grow py-2 bg-transparent" dense nav>
              <v-list-item
                v-for="(category, index) in orgRepo.categories"
                :key="index"
                @click="selectFolder(index)"
                :class="activeFolderIndex === index ? 'bg-blue-50 text-blue-900 font-semibold' : ''"
                class="rounded-lg mb-1 folder-item"
              >
                <v-list-item-icon class="mr-3 my-2">
                  <v-icon :color="activeFolderIndex === index ? 'blue darken-3' : 'grey darken-1'">
                    mdi-folder
                  </v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-sm font-medium">{{
                    category.categoryName
                  }}</v-list-item-title>
                </v-list-item-content>
                <v-list-item-action
                  class="my-0 flex flex-row justify-end items-center gap-1 action-buttons"
                >
                  <v-btn icon x-small @click.stop="editCategory(index)" title="Rename">
                    <v-icon color="blue darken-2" x-small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon x-small @click.stop="deleteCategory(index)" title="Delete">
                    <v-icon color="red lighten-1" x-small>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
                <v-chip
                  class="ml-2 font-semibold"
                  x-small
                  color="grey lighten-3"
                  text-color="grey darken-3"
                >
                  {{ category.content ? category.content.length : 0 }}
                </v-chip>
              </v-list-item>
            </v-list>
          </v-card>
        </v-col>

        <!-- Right Panel: Folder Content View -->
        <v-col cols="12" md="9">
          <v-card
            class="rounded-md border border-gray-100 h-full flex flex-column bg-white shadow-sm"
            flat
            outlined
          >
            <!-- Directory Header Bar -->
            <div
              class="pa-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4 bg-white rounded-t-xl"
            >
              <div>
                <div class="text-xs text-gray-500 flex items-center mb-1">
                  <span>{{ orgRepo.name || 'Repository' }}</span>
                  <v-icon x-small class="mx-1">mdi-chevron-right</v-icon>
                  <span class="font-medium text-gray-700">{{ activeFolder.categoryName }}</span>
                </div>
                <h2 class="text-lg font-bold text-gray-800 flex items-center">
                  <v-icon left color="amber darken-2" class="mr-2">mdi-folder-open</v-icon>
                  {{ activeFolder.categoryName }}
                </h2>
              </div>

              <div class="flex items-center justify-end gap-2">
                <v-text-field
                  v-model="searchQuery"
                  placeholder="Search files..."
                  prepend-inner-icon="mdi-magnify"
                  dense
                  outlined
                  hide-details
                  style="max-width: 200px"
                  class="rounded-lg bg-gray-50"
                ></v-text-field>

                <v-btn color="primary" class="normal-case rounded-lg" @click="openUploadFileDialog">
                  <v-icon left>mdi-upload</v-icon> Upload File
                </v-btn>

                <v-btn icon @click="showRepoSettingsDialog = true" title="Repository Settings">
                  <v-icon color="grey darken-1">mdi-cog</v-icon>
                </v-btn>
              </div>
            </div>

            <!-- Content Grid View -->
            <div class="flex-grow pa-4 bg-gray-50/30">
              <div
                v-if="filteredFiles.length === 0"
                class="flex flex-column items-center justify-center py-16 text-center"
              >
                <v-icon size="80" color="grey lighten-1">mdi-file-document-outline</v-icon>
                <h3 class="text-lg font-semibold text-gray-700 mt-4">No files found</h3>
                <p class="text-gray-500 text-sm max-w-sm mt-2">
                  {{
                    searchQuery
                      ? 'Try adjusting your search query'
                      : 'Upload files or add links to this directory to get started'
                  }}
                </p>
                <v-btn
                  v-if="!searchQuery"
                  color="primary"
                  class="mt-4 normal-case rounded-lg"
                  @click="openUploadFileDialog"
                >
                  <v-icon left>mdi-upload</v-icon> Upload first file
                </v-btn>
              </div>

              <v-row v-else>
                <v-col v-for="(file, index) in filteredFiles" :key="index" cols="12" sm="6" md="4">
                  <v-card
                    class="file-card rounded-xl border border-gray-100 hover:shadow-md transition duration-200"
                    flat
                    outlined
                  >
                    <!-- File Preview Area -->
                    <div
                      class="h-44 bg-gray-100/50 flex items-center justify-center overflow-hidden border-b border-gray-100 relative"
                    >
                      <!-- Image Thumbnail -->
                      <v-img
                        v-if="file.type === 'org-images'"
                        :src="getFullUrl(file.link)"
                        :alt="file.title"
                        height="100%"
                        contain
                        @click="openInNewTab(file.link)"
                        class="cursor-pointer w-full"
                      ></v-img>

                      <!-- YouTube Frame -->
                      <div
                        v-else-if="file.type === 'youtube-link'"
                        class="video-container w-full h-full"
                      >
                        <iframe
                          :src="getYoutubeEmbedUrl(file.link)"
                          frameborder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowfullscreen
                        ></iframe>
                      </div>

                      <!-- Document Icon -->
                      <div
                        v-else
                        class="w-full h-full flex items-center justify-center cursor-pointer bg-slate-50"
                        @click="openInNewTab(file.link)"
                      >
                        <div class="text-center p-4">
                          <v-icon size="56" :color="getFileColor(file.title || file.link)">
                            {{ getFileIcon(file.title || file.link) }}
                          </v-icon>
                        </div>
                      </div>
                    </div>

                    <!-- File Details -->
                    <v-card-text class="pa-4 bg-white">
                      <div class="flex justify-between items-start mb-2">
                        <div class="truncate mr-2">
                          <h3
                            class="font-bold text-gray-800 text-sm truncate mb-0.5"
                            :title="file.title"
                          >
                            {{ file.title }}
                          </h3>
                          <p class="text-xs text-gray-400 capitalize">
                            {{ file.type.replace('org-', '') }}
                          </p>
                        </div>
                        <div class="flex justify-end gap-1">
                          <v-btn icon x-small @click="copyItemLink(file)" title="Copy Link">
                            <v-icon small>mdi-link</v-icon>
                          </v-btn>
                          <v-btn icon x-small @click="editContent(index)" title="Rename Title">
                            <v-icon small color="blue">mdi-pencil</v-icon>
                          </v-btn>
                          <v-btn icon x-small @click="deleteContent(index)" title="Delete">
                            <v-icon small color="red lighten-1">mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </div>

                      <div
                        class="text-xs text-gray-500 flex flex-wrap justify-between items-center border-t border-gray-50 pt-2 gap-1"
                      >
                        <span
                          class="truncate font-medium text-gray-600 max-w-[120px]"
                          :title="
                            file.uploadedBy
                              ? `${file.uploadedBy.firstName || ''} ${
                                  file.uploadedBy.lastName || ''
                                }`.trim()
                              : ''
                          "
                        >
                          By:
                          {{
                            file.uploadedBy
                              ? `${file.uploadedBy.firstName || ''} ${
                                  file.uploadedBy.lastName || ''
                                }`.trim()
                              : 'System'
                          }}
                        </span>
                        <span>{{ formatDate(file.uploadedAt) }}</span>
                      </div>
                      <div class="text-xs text-gray-400 mt-1" v-if="file.size">
                        Size: {{ file.size }}
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <div v-else class="flex items-center justify-center py-12">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </div>
    </div>

    <!-- Create Repository Dialog -->
    <v-dialog v-model="showCreateRepoDialog" max-width="450">
      <v-card class="rounded-xl pa-2 bg-white">
        <v-card-title class="text-md font-bold text-gray-800">Create New Repository</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newRepoName"
            label="Repository Name"
            outlined
            dense
            hide-details
            class="mt-2 rounded-lg"
            placeholder="e.g. Sales Playbooks"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text class="normal-case rounded-lg" @click="showCreateRepoDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="primary" class="normal-case rounded-lg" @click="createRepository"
            >Create</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Repository Settings Dialog -->
    <v-dialog v-model="showRepoSettingsDialog" max-width="500">
      <v-card class="rounded-xl pa-2 bg-white" v-if="orgRepo">
        <v-card-title class="text-md font-bold text-gray-800">Repository Settings</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="orgRepo.name"
            label="Repository Name"
            outlined
            dense
            class="mt-2 rounded-lg"
          ></v-text-field>

          <v-switch
            v-model="orgRepo.expansionState"
            label="Always Expand Categories"
            dense
            color="primary"
          ></v-switch>
          <v-switch
            v-model="orgRepo.openInNewTab"
            label="Open documents in new tab"
            dense
            color="primary"
          ></v-switch>
          <v-switch
            v-model="orgRepo.visibleInDashboard"
            label="Show on Employee Dashboard"
            dense
            color="primary"
          ></v-switch>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text class="normal-case rounded-lg" @click="showRepoSettingsDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="primary" class="normal-case rounded-lg" @click="saveRepoSettings"
            >Save Settings</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Create / Rename Folder Dialog -->
    <v-dialog v-model="showAddCategoryDialog" max-width="450">
      <v-card class="rounded-xl pa-2 bg-white">
        <v-card-title class="text-md font-bold text-gray-800">
          {{ editingCategoryIndex !== null ? 'Rename Folder' : 'Create New Folder' }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newCategoryName"
            label="Folder Name"
            outlined
            dense
            hide-details
            class="mt-2 rounded-lg"
            placeholder="e.g. Sales Training"
            @keyup.enter="saveCategory"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text class="normal-case rounded-lg" @click="closeCategoryDialog">Cancel</v-btn>
          <v-btn color="primary" class="normal-case rounded-lg" @click="saveCategory">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Upload File / Add Content Dialog -->
    <v-dialog v-model="showUploadDialog" max-width="500">
      <v-card class="rounded-xl pa-2 bg-white">
        <v-card-title class="text-md font-bold text-gray-800"
          >Add Content to {{ activeFolder.categoryName }}</v-card-title
        >
        <v-card-text>
          <v-form ref="uploadForm" @submit.prevent="addContent">
            <v-text-field
              v-model="contentTitle"
              label="File Title"
              outlined
              dense
              class="mt-2 rounded-lg"
              placeholder="e.g. Product Demo Guide"
              required
            ></v-text-field>

            <v-select
              v-model="contentType"
              :items="contentTypeOptions"
              label="Content Type"
              outlined
              dense
              class="rounded-lg"
              required
            ></v-select>

            <!-- YouTube URL Input -->
            <v-text-field
              v-if="contentType === 'youtube-link'"
              v-model="contentLink"
              label="Youtube Link or Video ID"
              outlined
              dense
              class="rounded-lg"
              placeholder="Enter YouTube URL"
              required
            ></v-text-field>

            <!-- File Upload Box -->
            <div v-else class="mb-4">
              <div
                class="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-primary transition"
                @click="triggerFileInput(contentType === 'org-images' ? 'image' : 'document')"
              >
                <v-icon size="44" color="grey darken-1">mdi-cloud-upload</v-icon>
                <p class="text-gray-700 mt-2 font-medium">
                  {{ uploadedFile ? uploadedFile.name : 'Click to select file' }}
                </p>
                <p class="text-xs text-gray-400">Max size: 10MB</p>
              </div>

              <!-- Hidden file inputs -->
              <input
                ref="imageInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleFileUpload($event, 'image')"
              />
              <input
                ref="documentInput"
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                class="hidden"
                @change="handleFileUpload($event, 'document')"
              />
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text class="normal-case rounded-lg" @click="showUploadDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="primary" class="normal-case rounded-lg" @click="addContent">Submit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit File / Link Dialog -->
    <v-dialog v-model="showEditContentDialog" max-width="500">
      <v-card class="rounded-xl pa-2 bg-white">
        <v-card-title class="text-md font-bold text-gray-800">Edit Title</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editContentData.title"
            label="File Title"
            outlined
            dense
            class="mt-2 rounded-lg"
          ></v-text-field>
          <v-text-field
            v-if="editContentData.type === 'youtube-link'"
            v-model="editContentData.link"
            label="Youtube Video Link / ID"
            outlined
            dense
            class="rounded-lg"
          ></v-text-field>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text class="normal-case rounded-lg" @click="showEditContentDialog = false"
            >Cancel</v-btn
          >
          <v-btn color="primary" class="normal-case rounded-lg" @click="updateContent"
            >Update</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import NoData from '../../../NoData.vue'

export default {
  name: 'OrgDriveExplorer',
  components: {
    NoData
  },
  data() {
    return {
      loading: false,
      activeFolderIndex: 0,
      searchQuery: '',
      userData: this.$storage.getUniversal('user') || {},
      s3BaseUrl: process.env.VUE_APP_S3_BASE_URL || '',

      // Repository Structure
      orgRepo: {
        name: '',
        enable: false,
        expansionState: false,
        openInNewTab: false,
        visibleInDashboard: false,
        categories: []
      },

      // Upload controls
      showUploadDialog: false,
      contentType: 'youtube-link',
      contentTitle: '',
      contentLink: '',
      uploadedFile: null,
      contentTypeOptions: [
        { text: 'Youtube Link', value: 'youtube-link' },
        { text: 'Image File', value: 'org-images' },
        { text: 'Document (PDF, Word, Excel, etc.)', value: 'org-docs' }
      ],

      // Dialog controls
      showCreateRepoDialog: false,
      showRepoSettingsDialog: false,
      showAddCategoryDialog: false,
      showEditContentDialog: false,
      newRepoName: '',
      newCategoryName: '',
      editingCategoryIndex: null,

      editContentData: {
        index: null,
        title: '',
        type: '',
        link: ''
      }
    }
  },
  computed: {
    activeFolder() {
      return (
        this.orgRepo.categories[this.activeFolderIndex] || { categoryName: 'General', content: [] }
      )
    },
    filteredFiles() {
      const contentList = this.activeFolder.content || []
      if (!this.searchQuery) return contentList
      const query = this.searchQuery.toLowerCase()
      return contentList.filter((c) => c.title.toLowerCase().includes(query))
    },
    hasRepository() {
      return this.orgRepo && this.orgRepo.enable
    }
  },
  async mounted() {
    await this.loadRepoData()
  },
  methods: {
    async loadRepoData() {
      this.loading = true
      try {
        const res = await this.$axios.get('/orgSetup/empapp-settings')
        const empApp = res.data.empApp || {}

        if (empApp.orgRepo) {
          this.orgRepo = empApp.orgRepo
        } else if (empApp.orgTutorial) {
          const oldData = empApp.orgTutorial
          this.orgRepo = {
            name: 'Migrated Repository',
            enable: oldData.enable || true,
            expansionState: false,
            openInNewTab: false,
            visibleInDashboard: false,
            categories: [
              {
                categoryName: 'General',
                content: oldData.content.map((item) => ({
                  title: item.title,
                  type: this.migrateContentType(item.type),
                  link: item.link
                }))
              }
            ]
          }
          await this.saveRepoData()
        }

        // Initialize default folders/categories if none are present
        if (!this.orgRepo.categories || this.orgRepo.categories.length === 0) {
          this.orgRepo.categories = [
            { categoryName: 'General', content: [] },
            { categoryName: 'Important', content: [] },
            { categoryName: 'Certificates', content: [] }
          ]
          await this.saveRepoData()
        }
      } catch (error) {
        console.error('Error fetching repository settings:', error)
      } finally {
        this.loading = false
      }
    },
    migrateContentType(oldType) {
      switch (oldType) {
        case 'image':
          return 'org-images'
        case 'link':
          return 'youtube-link'
        default:
          return 'org-docs'
      }
    },
    async saveRepoData() {
      try {
        const payload = {
          orgRepo: this.orgRepo
        }
        await this.$axios.post('/orgSetup/empapp-settings', payload)
      } catch (error) {
        console.error('Error saving repository:', error)
        this.$toast.error('Failed to save changes')
      }
    },
    selectFolder(index) {
      this.activeFolderIndex = index
      this.searchQuery = ''
    },
    openUploadFileDialog() {
      this.contentTitle = ''
      this.contentType = 'org-docs'
      this.contentLink = ''
      this.uploadedFile = null
      this.showUploadDialog = true
    },
    async openCreateRepoDialog() {
      this.newRepoName = ''
      this.showCreateRepoDialog = true
    },
    async createRepository() {
      if (!this.newRepoName.trim()) {
        this.$toast.error('Repository name is required')
        return
      }
      this.orgRepo = {
        name: this.newRepoName,
        enable: true,
        expansionState: false,
        openInNewTab: false,
        visibleInDashboard: false,
        categories: [
          { categoryName: 'General', content: [] },
          { categoryName: 'Important', content: [] },
          { categoryName: 'Certificates', content: [] }
        ]
      }
      await this.saveRepoData()
      this.showCreateRepoDialog = false
      this.newRepoName = ''
    },
    async saveRepoSettings() {
      if (!this.orgRepo.name.trim()) {
        this.$toast.error('Repository name cannot be empty')
        return
      }
      await this.saveRepoData()
      this.$toast.success('Repository settings saved successfully')
      this.showRepoSettingsDialog = false
    },
    closeCategoryDialog() {
      this.showAddCategoryDialog = false
      this.newCategoryName = ''
      this.editingCategoryIndex = null
    },
    async saveCategory() {
      if (!this.newCategoryName.trim()) {
        this.$toast.error('Folder name cannot be empty')
        return
      }

      if (this.editingCategoryIndex !== null) {
        this.orgRepo.categories[this.editingCategoryIndex].categoryName = this.newCategoryName
      } else {
        const exists = this.orgRepo.categories.some(
          (c) => c.categoryName.toLowerCase() === this.newCategoryName.trim().toLowerCase()
        )
        if (exists) {
          this.$toast.error('Folder already exists')
          return
        }
        this.orgRepo.categories.push({
          categoryName: this.newCategoryName.trim(),
          content: []
        })
      }

      await this.saveRepoData()
      this.$toast.success('Folder settings saved')
      this.closeCategoryDialog()
    },
    editCategory(index) {
      this.editingCategoryIndex = index
      this.newCategoryName = this.orgRepo.categories[index].categoryName
      this.showAddCategoryDialog = true
    },
    async deleteCategory(index) {
      if (
        confirm(
          `Are you sure you want to delete folder "${this.orgRepo.categories[index].categoryName}" and all its files?`
        )
      ) {
        this.orgRepo.categories.splice(index, 1)
        if (this.activeFolderIndex >= this.orgRepo.categories.length) {
          this.activeFolderIndex = Math.max(0, this.orgRepo.categories.length - 1)
        }
        await this.saveRepoData()
        this.$toast.success('Folder deleted successfully')
      }
    },
    triggerFileInput(type) {
      if (type === 'image') {
        this.$refs.imageInput.click()
      } else {
        this.$refs.documentInput.click()
      }
    },
    handleFileUpload(event, type) {
      const file = event.target.files[0]
      if (!file) return

      this.uploadedFile = file
      if (!this.contentTitle) {
        this.contentTitle = file.name
      }
    },
    async addContent() {
      if (!this.contentTitle || !this.contentType) {
        this.$toast.error('Please fill all fields')
        return
      }

      let fileLink = this.contentLink
      let size = null

      if (this.contentType !== 'youtube-link') {
        if (!this.uploadedFile) {
          this.$toast.error('Please select a file to upload')
          return
        }

        const formData = new FormData()
        formData.append('attachments', this.uploadedFile)
        formData.append('type', this.activeFolder.categoryName)

        this.$toast.info('Uploading file...')
        try {
          const response = await this.$axios.post('/org/logo', formData)
          if (response.data.success && response.data.url) {
            fileLink = response.data.url.doc_physical_path
            size = this.formatBytes(this.uploadedFile.size)
          } else {
            this.$toast.error('Failed to upload file')
            return
          }
        } catch (error) {
          console.error('Error uploading file:', error)
          this.$toast.error('Error uploading file')
          return
        }
      } else {
        if (!this.contentLink) {
          this.$toast.error('Please enter YouTube link')
          return
        }
      }

      const newContent = {
        title: this.contentTitle,
        type: this.contentType,
        link: fileLink,
        size,
        uploadedAt: new Date(),
        uploadedBy: {
          _id: this.userData?._id || '',
          firstName: this.userData?.firstName || 'System',
          lastName: this.userData?.lastName || ''
        }
      }

      if (!this.activeFolder.content) {
        this.$set(this.activeFolder, 'content', [])
      }

      this.activeFolder.content.push(newContent)
      await this.saveRepoData()
      this.showUploadDialog = false
      this.contentTitle = ''
      this.contentLink = ''
      this.uploadedFile = null
      this.$toast.success('File added to directory successfully')
    },
    editContent(itemIndex) {
      const file = this.activeFolder.content[itemIndex]
      this.editContentData = {
        index: itemIndex,
        title: file.title,
        type: file.type,
        link: file.link
      }
      this.showEditContentDialog = true
    },
    async updateContent() {
      const { index, title, link } = this.editContentData
      const file = this.activeFolder.content[index]
      file.title = title
      if (file.type === 'youtube-link') {
        file.link = link
      }
      this.showEditContentDialog = false
      await this.saveRepoData()
      this.$toast.success('File renamed successfully')
    },
    async deleteContent(index) {
      if (confirm('Are you sure you want to delete this file from the folder?')) {
        this.activeFolder.content.splice(index, 1)
        await this.saveRepoData()
        this.$toast.success('File deleted successfully')
      }
    },
    copyItemLink(file) {
      const url = this.getFullUrl(file.link)
      navigator.clipboard
        .writeText(url)
        .then(() => {
          this.$toast.success('Link copied to clipboard!')
        })
        .catch((err) => {
          console.error('Could not copy link:', err)
        })
    },
    getYoutubeVideoId(url) {
      const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
      const match = url.match(regExp)
      return match && match[2].length === 11 ? match[2] : null
    },
    getYoutubeEmbedUrl(url) {
      const videoId = url.includes('/') ? this.getYoutubeVideoId(url) : url
      return `https://www.youtube.com/embed/${videoId}`
    },
    getFullUrl(link) {
      if (!link) return ''
      if (link.startsWith('http')) return link
      return this.s3BaseUrl + link
    },
    openInNewTab(link) {
      const url = this.getFullUrl(link)
      window.open(url, '_blank')
    },
    getFileIcon(filename) {
      if (!filename) return 'mdi-file-document'
      const ext = filename.split('.').pop().toLowerCase()
      switch (ext) {
        case 'pdf':
          return 'mdi-file-pdf-box'
        case 'doc':
        case 'docx':
          return 'mdi-file-word'
        case 'xls':
        case 'xlsx':
          return 'mdi-file-excel'
        case 'png':
        case 'jpg':
        case 'jpeg':
        case 'gif':
          return 'mdi-file-image'
        default:
          return 'mdi-file-document'
      }
    },
    getFileColor(filename) {
      if (!filename) return 'grey darken-1'
      const ext = filename.split('.').pop().toLowerCase()
      switch (ext) {
        case 'pdf':
          return 'red darken-2'
        case 'doc':
        case 'docx':
          return 'blue'
        case 'xls':
        case 'xlsx':
          return 'green'
        case 'png':
        case 'jpg':
        case 'jpeg':
        case 'gif':
          return 'purple'
        default:
          return 'grey darken-1'
      }
    },
    formatBytes(bytes, decimals = 2) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const dm = decimals < 0 ? 0 : decimals
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.hidden {
  display: none;
}
.folder-item {
  transition: background-color 0.15s ease-in-out;
}
.folder-item .action-buttons {
  opacity: 0;
  transition: opacity 0.15s ease-in-out;
}
.folder-item:hover .action-buttons {
  opacity: 1;
}
.file-card {
  transition: all 0.2s ease-in-out;
}
.file-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border-color: #21427d !important;
}
.video-container {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
}
.video-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>
